import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Mail, User, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function Contact() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const valid = () =>
    form.name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) &&
    form.subject.trim().length >= 3 &&
    form.message.trim().length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid()) {
      setError('Please fill in every field — message must be at least 10 characters.');
      return;
    }
    setBusy(true); setError('');
    try {
      await api.post('/contact', {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      });
      setSent(true);
      toast.success('Message sent — we\'ll get back to you soon ✨');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Could not send your message. Please try again.';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg relative" data-testid="contact-page">
      <div className="stars-bg" />
      <div className="relative max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/"
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center"
            data-testid="contact-back"
            aria-label="Back to home"
          >
            <ArrowLeft size={16} />
          </Link>
          <Logo size="md" showText />
        </div>

        <div className="rounded-3xl p-7 md:p-9 bg-[#161622]/80 backdrop-blur border border-white/10">
          {sent ? (
            <div className="text-center py-6" data-testid="contact-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
                <CheckCircle2 size={28} className="text-emerald-300" />
              </div>
              <h1 className="font-display font-bold text-2xl mt-5 text-white">Message sent ✨</h1>
              <p className="text-sm text-white/65 mt-2 leading-relaxed">
                Thanks for reaching out, <span className="text-white">{form.name.split(' ')[0]}</span>. The WorldVibe team will reply to <span className="text-cyan-300">{form.email}</span> within 1–2 business days.
              </p>
              <div className="mt-7 flex items-center justify-center gap-2">
                <Button
                  onClick={() => { setForm({ name: '', email: '', subject: '', message: '' }); setSent(false); }}
                  variant="outline"
                  className="border-white/15 bg-white/5 hover:bg-white/10 text-white rounded-full"
                  data-testid="contact-send-another"
                >
                  Send another
                </Button>
                <Button
                  onClick={() => nav('/')}
                  className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 rounded-full font-semibold"
                  data-testid="contact-back-home"
                >
                  Back to home
                </Button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-display font-bold text-3xl md:text-4xl bg-gradient-to-r from-fuchsia-300 via-pink-300 to-cyan-300 bg-clip-text text-transparent">
                Contact us
              </h1>
              <p className="text-sm text-white/60 mt-2 leading-relaxed">
                Got a question, partnership idea, or feedback? Drop us a line and we&apos;ll get back to you as soon as the WorldVibe crew lands at HQ.
              </p>

              <form onSubmit={submit} className="space-y-4 mt-7" data-testid="contact-form">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-white/50">Your name</Label>
                  <div className="relative mt-1.5">
                    <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="What should we call you?"
                      data-testid="contact-name"
                      maxLength={80}
                      className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-white/50">Your email</Label>
                  <div className="relative mt-1.5">
                    <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@example.com"
                      data-testid="contact-email"
                      maxLength={200}
                      className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-white/50">Subject</Label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="What's this about?"
                    data-testid="contact-subject"
                    maxLength={140}
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                  />
                </div>

                <div>
                  <Label className="text-[11px] uppercase tracking-wider text-white/50">
                    Message <span className="lowercase text-white/30">(min 10 characters)</span>
                  </Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us more…"
                    rows={6}
                    maxLength={2000}
                    data-testid="contact-message"
                    className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-white/40 min-h-[130px]"
                  />
                  <div className="text-[10px] text-white/30 text-right mt-1">{form.message.length}/2000</div>
                </div>

                {error && (
                  <div className="text-sm text-rose-400 rounded-lg px-3 py-2 bg-rose-500/10 border border-rose-500/20" data-testid="contact-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={busy || !valid()}
                  data-testid="contact-submit"
                  className="w-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:opacity-90 text-white border-0 font-semibold disabled:opacity-40"
                >
                  {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : <Send className="mr-2" size={16} />}
                  Send message
                </Button>
              </form>
            </>
          )}
        </div>

        <div className="mt-8 text-center text-xs text-white/30">
          WorldVibe · <span className="text-white/50 italic">Meet the World in Real Time</span>
        </div>
      </div>
    </div>
  );
}
